import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtL, apiErr } from "@/lib/format";
import {
  XIcon, MagnifyingGlassIcon, PackageIcon, FlowerIcon,
  CheckCircleIcon, InfoIcon,
} from "@phosphor-icons/react";
import BarcodeScanInput from "@/components/BarcodeScanInput";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * <QuickReceiveModal />
 * Registro rápido de ingreso de un producto YA EXISTENTE.
 * Captura: batch, lote de fabricación, cantidad, precio de compra, fechas, bodega +
 * opcionalmente actualiza precios de venta P0-P4 desde el mismo formulario.
 *
 * Endpoint: POST /api/inventory/quick-receive
 */
export default function QuickReceiveModal({ onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    cantidad: "",
    precio_compra: "",
    descripcion: "",
    batch: "",
    lote_fabricacion: "",
    fecha_fabricacion: "",
    fecha_vencimiento: "",
    fecha_test: "",
    fecha_ingreso: new Date().toISOString().slice(0, 10),
    bodega_id: "",
    num_factura_proveedor: "",
    cai_proveedor: "",
    proveedor_id: "",
    ubicacion: "",
    notas: "",
  });

  // Precios de venta editables (se prellenan al seleccionar producto)
  const [updatePrices, setUpdatePrices] = useState(false);
  const [prices, setPrices] = useState({ p0: "", p1: "", p2: "", p3: "", p4: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: prods }, { data: whs }, { data: sups }] = await Promise.all([
          api.get("/products"),
          api.get("/warehouses", { params: { activo: true } }),
          api.get("/suppliers").catch(() => ({ data: [] })),
        ]);
        setProducts(prods || []);
        setSuppliers(sups || []);
        const usable = (whs || []).filter((w) => w.tipo !== "transito");
        setWarehouses(usable);
        const principal = usable.find((w) => w.tipo === "principal");
        if (principal) setForm((f) => ({ ...f, bodega_id: principal.id }));
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products.slice(0, 20);
    return products.filter((p) =>
      (p.codigo || "").toLowerCase().includes(s) ||
      (p.descripcion || "").toLowerCase().includes(s) ||
      (p.nombre || "").toLowerCase().includes(s) ||
      (p.codigo_barras || "").toLowerCase().includes(s)
    ).slice(0, 30);
  }, [q, products]);

  const pick = (p) => {
    setSelected(p);
    setPrices({
      p0: p.precio_p0 || "",
      p1: p.precio_p1 || "",
      p2: p.precio_p2 || "",
      p3: p.precio_p3 || "",
      p4: p.precio_p4 || "",
    });
    // Sugerir precio de compra basado en último costo + prellenar descripción existente
    setForm((f) => ({
      ...f,
      precio_compra: p.ultimo_costo || p.costo_promedio || "",
      descripcion: p.descripcion || p.nombre || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Selecciona un producto"); return; }
    if (!Number(form.cantidad) || Number(form.cantidad) <= 0) { toast.error("Cantidad debe ser mayor a 0"); return; }
    if (Number(form.precio_compra) < 0) { toast.error("Precio de compra inválido"); return; }

    const payload = {
      producto_id: selected.id,
      cantidad: Number(form.cantidad),
      precio_compra: Number(form.precio_compra),
      descripcion: form.descripcion || "",
      fecha_ingreso: form.fecha_ingreso || null,
      batch: form.batch, lote_fabricacion: form.lote_fabricacion,
      fecha_fabricacion: form.fecha_fabricacion || null,
      fecha_vencimiento: form.fecha_vencimiento || null,
      fecha_test: form.fecha_test || null,
      bodega_id: form.bodega_id || null,
      num_factura_proveedor: form.num_factura_proveedor,
      cai_proveedor: form.cai_proveedor,
      proveedor_id: form.proveedor_id || null,
      ubicacion: form.ubicacion || "",
      notas: form.notas,
    };
    if (updatePrices) {
      const upd = {};
      ["p0", "p1", "p2", "p3", "p4"].forEach((k) => {
        if (prices[k] !== "" && prices[k] !== null && !isNaN(Number(prices[k]))) upd[k] = Number(prices[k]);
      });
      if (Object.keys(upd).length > 0) payload.actualizar_precios_venta = upd;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/quick-receive", payload);
      toast.success(data.message || "Ingreso registrado");
      onSaved && onSaved(data);
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const qm = useModalForm({ value: { s: selected?.id, form, updatePrices, prices }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...qm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quick-receive-modal">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-5 flex justify-between items-start z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-green">Ingreso rápido</div>
            <h3 className="font-heading text-lg font-semibold">Registrar producto comprado nuevamente</h3>
            <div className="text-xs text-text-muted mt-1">Captura del lote, precio de compra y (opcional) actualiza el precio de venta.</div>
          </div>
          <button aria-label="Cerrar" onClick={qm.cerrar} className="text-text-muted hover:text-text-primary" data-testid="quick-close"><XIcon size={20} /></button>
        </div>

        <form onSubmit={qm.guardar} className="p-5 space-y-5">
          {/* 1. Selector de producto */}
          {!selected ? (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-2 block">1) Seleccionar producto</label>
              <div className="mb-2">
                <BarcodeScanInput testid="quick-barcode" onFound={(p) => pick(p)} placeholder="Escanear código de barras (lector USB) y Enter…" />
              </div>
              <div className="relative mb-3">
                <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                       placeholder="Buscar por código o descripción…"
                       className="w-full pl-9 pr-3 py-2.5 bg-app border border-border rounded-md text-sm focus:border-agro-green"
                       data-testid="quick-search-product" />
              </div>
              <div className="max-h-72 overflow-y-auto border border-border/60 rounded-md divide-y divide-border/40" data-testid="quick-product-list">
                {filtered.length === 0 ? (
                  <div className="p-6 text-center text-text-muted text-sm">Sin resultados</div>
                ) : filtered.map((p) => (
                  <button key={p.id} type="button" onClick={() => pick(p)}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/[0.03] flex items-center gap-3"
                    data-testid={`quick-pick-${p.codigo}`}>
                    {p.es_semilla
                      ? <FlowerIcon size={16} weight="fill" className="text-agro-yellow flex-shrink-0" />
                      : <PackageIcon size={16} className="text-agro-green flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-agro-green">{p.codigo}</div>
                      <div className="text-sm truncate">{p.descripcion || p.nombre}</div>
                    </div>
                    <div className="text-right text-xs whitespace-nowrap">
                      <div className="text-text-muted">Stock: <span className="text-text-primary">{p.stock_actual || 0}</span></div>
                      <div className="text-text-muted">PC: <span className="text-agro-yellow">{fmtL(p.costo_promedio || 0)}</span></div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Producto seleccionado — resumen */}
              <div className="bg-agro-green/5 border border-agro-green/30 rounded-md p-3 flex items-center gap-3" data-testid="quick-selected-product">
                {selected.es_semilla
                  ? <FlowerIcon size={20} weight="fill" className="text-agro-yellow" />
                  : <PackageIcon size={20} weight="duotone" className="text-agro-green" />}
                <div className="flex-1">
                  <div className="font-mono text-xs text-agro-green">{selected.codigo}</div>
                  <div className="text-sm font-medium">{selected.descripcion || selected.nombre}</div>
                  <div className="text-[10px] text-text-muted">Stock actual: {selected.stock_actual || 0} · PC ponderado: {fmtL(selected.costo_promedio || 0)} · Último costo: {fmtL(selected.ultimo_costo || 0)}</div>
                </div>
                <button type="button" onClick={() => { setSelected(null); setQ(""); }} className="text-xs text-text-muted hover:text-agro-yellow" data-testid="quick-change-product">Cambiar</button>
              </div>

              {/* Descripción del producto (para factura) */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block flex items-center gap-1.5">
                  Descripción del producto <span className="text-agro-yellow">(aparece en la factura)</span>
                </label>
                <textarea
                  rows={2}
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Descripción que se imprimirá en la factura de venta (ej. 'Urea Granulada 46% N · saco 50 kg')"
                  data-testid="quick-descripcion"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                />
                <p className="text-[10px] text-text-muted mt-1">Se guarda en el catálogo del producto y se usa por defecto al facturar. Podés editarla por línea al hacer la factura.</p>
              </div>

              {/* 2. Datos del ingreso */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-2 block">2) Datos del ingreso (lote nuevo)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <FieldLbl label="Cantidad *">
                    <input type="number" required min="0.01" step="any" value={form.cantidad}
                           onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-cantidad" />
                  </FieldLbl>
                  <FieldLbl label="Precio de compra unitario *">
                    <input type="number" required min="0" step="any" value={form.precio_compra}
                           onChange={(e) => setForm({ ...form, precio_compra: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-precio-compra" />
                  </FieldLbl>
                  <FieldLbl label="Bodega destino">
                    <select value={form.bodega_id} onChange={(e) => setForm({ ...form, bodega_id: e.target.value })}
                            className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                            data-testid="quick-bodega">
                      <option value="">— Selecciona —</option>
                      {warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo} — {w.nombre}</option>)}
                    </select>
                  </FieldLbl>
                  <FieldLbl label="Fecha de ingreso" hint="Determina el orden PEPS al facturar">
                    <input type="date" value={form.fecha_ingreso}
                           onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-fecha-ingreso" />
                  </FieldLbl>
                  <FieldLbl label="Batch">
                    <input type="text" value={form.batch} placeholder="B-001"
                           onChange={(e) => setForm({ ...form, batch: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-batch" />
                  </FieldLbl>
                  <FieldLbl label="Lote de fabricación">
                    <input type="text" value={form.lote_fabricacion} placeholder="LF-2026-001"
                           onChange={(e) => setForm({ ...form, lote_fabricacion: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-lote-fab" />
                  </FieldLbl>
                  {selected.es_semilla ? (
                    <>
                      <FieldLbl label="Fecha de test (germinación)" hint="Define el orden PEPS de semillas">
                        <input type="date" value={form.fecha_test}
                               onChange={(e) => setForm({ ...form, fecha_test: e.target.value })}
                               className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                               data-testid="quick-fecha-test" />
                      </FieldLbl>
                      <FieldLbl label="Fecha vencimiento (opcional)">
                        <input type="date" value={form.fecha_vencimiento}
                               onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
                               className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                               data-testid="quick-fecha-venc" />
                      </FieldLbl>
                    </>
                  ) : (
                    <>
                      <FieldLbl label="Fecha fabricación">
                        <input type="date" value={form.fecha_fabricacion}
                               onChange={(e) => setForm({ ...form, fecha_fabricacion: e.target.value })}
                               className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                               data-testid="quick-fecha-fab" />
                      </FieldLbl>
                      <FieldLbl label="Fecha vencimiento">
                        <input type="date" value={form.fecha_vencimiento}
                               onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
                               className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                               data-testid="quick-fecha-venc" />
                      </FieldLbl>
                    </>
                  )}
                  <FieldLbl label="Proveedor del ingreso">
                    <select value={form.proveedor_id} onChange={(e) => setForm({ ...form, proveedor_id: e.target.value })}
                            className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                            data-testid="quick-proveedor">
                      <option value="">— Sin proveedor —</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.codigo ? `${s.codigo} · ` : ""}{s.nombre}</option>)}
                    </select>
                  </FieldLbl>
                  <FieldLbl label="Ubicación en bodega" hint="Pasillo / estante / nivel">
                    <input type="text" value={form.ubicacion} placeholder="P3-E2"
                           onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-ubicacion" />
                  </FieldLbl>
                  <FieldLbl label="N° factura proveedor">
                    <input type="text" value={form.num_factura_proveedor} placeholder="000-001-01-XXXX"
                           onChange={(e) => setForm({ ...form, num_factura_proveedor: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-num-factura" />
                  </FieldLbl>
                  <FieldLbl label="CAI proveedor">
                    <input type="text" value={form.cai_proveedor}
                           onChange={(e) => setForm({ ...form, cai_proveedor: e.target.value })}
                           className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                           data-testid="quick-cai" />
                  </FieldLbl>
                </div>
                {form.cantidad && form.precio_compra && (
                  <div className="mt-3 text-xs text-agro-green bg-agro-green/5 border border-agro-green/20 rounded px-3 py-2 inline-flex items-center gap-2">
                    <InfoIcon size={12} /> Costo total del ingreso: <b>{fmtL(Number(form.cantidad) * Number(form.precio_compra))}</b> · PC ponderado se recalculará automáticamente.
                  </div>
                )}
              </div>

              {/* 3. Actualizar precios de venta */}
              <div className="border border-border/60 rounded-md p-3" data-testid="quick-price-section">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={updatePrices}
                         onChange={(e) => setUpdatePrices(e.target.checked)}
                         className="w-4 h-4 accent-agro-green"
                         data-testid="quick-toggle-prices" />
                  <span className="text-sm font-medium">3) Actualizar precios de venta del producto</span>
                  <span className="text-[10px] text-text-muted">— cambia los precios que se aplicarán a futuras ventas</span>
                </label>
                {updatePrices && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                    {["p0", "p1", "p2", "p3", "p4"].map((k) => (
                      <FieldLbl key={k} label={`Precio ${k.toUpperCase()}${k === "p0" ? " (especial)" : ""}`}>
                        <input type="number" min="0" step="any" value={prices[k]}
                               onChange={(e) => setPrices({ ...prices, [k]: e.target.value })}
                               className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm focus:border-agro-green"
                               data-testid={`quick-price-${k}`} />
                      </FieldLbl>
                    ))}
                  </div>
                )}
              </div>

              {/* Notas */}
              <FieldLbl label="Notas (opcional)">
                <textarea value={form.notas} rows={2}
                          onChange={(e) => setForm({ ...form, notas: e.target.value })}
                          className="w-full bg-app border border-border rounded px-3 py-2 text-sm focus:border-agro-green"
                          placeholder="Ej: llegada parcial de camión, mercadería a bodega 2, etc."
                          data-testid="quick-notas" />
              </FieldLbl>

              <button type="submit" disabled={(saving) || qm.saving} data-testid="quick-submit"
                className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 transition disabled:opacity-50 flex items-center justify-center gap-2">
                <CheckCircleIcon size={18} weight="fill" /> {saving ? "Registrando…" : "Confirmar ingreso"}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

function FieldLbl({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1 block">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-text-muted mt-0.5">{hint}</div>}
    </div>
  );
}
